export type Lang = "en" | "ar";

export interface ChatTranslations {
  dir: "ltr" | "rtl";
  locale: string;
  headerTitle: string;
  headerSubtitle: string;
  langToggleLabel: string;
  fabHint: string;
  greeting: string;
  menuTrackOrder: string;
  menuKits: string;
  menuPalettes: string;
  menuWorkshops: string;
  menuShipping: string;
  menuSomethingElse: string;
  backToMenu: string;
  trackOrderPrompt: string;
  orderNotFound: (ref: string) => string;
  orderFoundIntro: (ref: string) => string;
  statusConfirmed: string;
  statusCancelled: string;
  statusPending: string;
  statusMixed: string;
  paidLabel: string;
  notPaidLabel: string;
  totalLabel: string;
  placedOnLabel: string;
  qtyLabel: string;
  statusLabelPrefix: string;
  paymentLabelPrefix: string;
  lookupError: string;
  kitsIntro: string;
  kitsEmpty: string;
  kitsError: string;
  viewAllKits: string;
  palettesIntro: string;
  palettesEmpty: string;
  palettesError: string;
  viewAllPalettes: string;
  workshopsIntro: string;
  workshopsEmpty: string;
  workshopsError: string;
  viewAllWorkshops: string;
  spotsLeft: (n: number) => string;
  fullyBooked: string;
  shippingPrompt: string;
  shippingSelectPlaceholder: string;
  shippingResult: (name: string, cost: number) => string;
  shippingNotFound: string;
  shippingError: string;
  contactIntro: string;
  fallbackIntro: string;
  fallbackUnknown: string;
  inputPlaceholder: string;
  closeChat: string;
  openChat: string;
}

export const translations: Record<Lang, ChatTranslations> = {
  en: {
    dir: "ltr",
    locale: "en-US",
    headerTitle: "Artsy Icile Assistant",
    headerSubtitle: "Usually replies instantly",
    langToggleLabel: "العربية",
    fabHint: "Need help? Chat with me! 👋",
    greeting:
      "Hi! 👋 I'm the Artsy Icile assistant. I can help you track an order, browse our kits and palettes, check workshop dates, or look up shipping costs.",
    menuTrackOrder: "Track My Order",
    menuKits: "Paint Kits",
    menuPalettes: "Color Palettes",
    menuWorkshops: "Workshops",
    menuShipping: "Shipping Cost",
    menuSomethingElse: "Something Else",
    backToMenu: "What else can I help you with?",
    trackOrderPrompt: "Sure! Please enter your order reference number (e.g. PK-7K4XQ2).",
    orderNotFound: (ref) =>
      `I couldn't find an order with reference number "${ref}". Please double-check it, or reach us on WhatsApp / email below.`,
    orderFoundIntro: (ref) => `Here's what I found for ${ref}:`,
    statusConfirmed: "confirmed ✅",
    statusCancelled: "cancelled ❌",
    statusPending: "pending review ⏳",
    statusMixed: "mixed — items have different statuses",
    paidLabel: "Paid",
    notPaidLabel: "Not marked as paid yet",
    totalLabel: "Total",
    placedOnLabel: "Placed on",
    qtyLabel: "Qty",
    statusLabelPrefix: "Status",
    paymentLabelPrefix: "Payment",
    lookupError: "Sorry, something went wrong looking that up. Please try again in a moment.",
    kitsIntro: "Here are some of our paint kits:",
    kitsEmpty: "We don't have any kits listed right now — check back soon!",
    kitsError: "Sorry, I couldn't load the kits right now. Please try again shortly.",
    viewAllKits: "View All Paint Kits",
    palettesIntro: "Here are some of our color palettes:",
    palettesEmpty: "We don't have any color palettes listed right now — check back soon!",
    palettesError: "Sorry, I couldn't load the palettes right now. Please try again shortly.",
    viewAllPalettes: "View All Color Palettes",
    workshopsIntro: "Here are our upcoming workshops:",
    workshopsEmpty: "There aren't any workshops scheduled right now — check back soon!",
    workshopsError: "Sorry, I couldn't load the workshops right now. Please try again shortly.",
    viewAllWorkshops: "View All Workshops",
    spotsLeft: (n) => `${n} spots left`,
    fullyBooked: "Fully booked",
    shippingPrompt: "Which governorate are you shipping to?",
    shippingSelectPlaceholder: "Select a governorate...",
    shippingResult: (name, cost) => `Shipping to ${name} costs EGP ${cost}.`,
    shippingNotFound: "Sorry, I couldn't find shipping info for that governorate.",
    shippingError: "Sorry, I couldn't load shipping info right now. Please try again shortly.",
    contactIntro: "Here's how to reach our team directly:",
    fallbackIntro:
      "I can help with order tracking, our paint kits, color palettes, workshops, and shipping costs. For anything else, our team is happy to help directly:",
    fallbackUnknown:
      "Sorry, I'm not able to answer that. You can reach us on WhatsApp or email below, or pick one of the options.",
    inputPlaceholder: "Type your question...",
    closeChat: "Close chat",
    openChat: "Open chat",
  },
  ar: {
    dir: "rtl",
    locale: "ar-EG",
    headerTitle: "مساعد Artsy Icile",
    headerSubtitle: "بيرد فورًا عادةً",
    langToggleLabel: "English",
    fabHint: "محتاج مساعدة؟ اتكلم معايا! 👋",
    greeting:
      "أهلاً! 👋 أنا مساعد Artsy Icile. أقدر أساعدك في تتبع طلبك، تصفح الأطقم والباليتات، معرفة مواعيد الورش، أو تكلفة الشحن.",
    menuTrackOrder: "تتبع طلبي",
    menuKits: "أطقم الرسم",
    menuPalettes: "باليتات الألوان",
    menuWorkshops: "الورش",
    menuShipping: "تكلفة الشحن",
    menuSomethingElse: "حاجة تانية",
    backToMenu: "أقدر أساعدك في حاجة تانية؟",
    trackOrderPrompt: "تمام! من فضلك اكتب رقم مرجع الطلب (مثال: PK-7K4XQ2).",
    orderNotFound: (ref) =>
      `معلش، مش لاقي طلب برقم "${ref}". تأكد من الرقم، أو تواصل معانا على واتساب / إيميل تحت.`,
    orderFoundIntro: (ref) => `دي تفاصيل الطلب ${ref}:`,
    statusConfirmed: "مؤكد ✅",
    statusCancelled: "ملغي ❌",
    statusPending: "قيد المراجعة ⏳",
    statusMixed: "متفاوت — الحالة مختلفة لكل عنصر",
    paidLabel: "متدفوع",
    notPaidLabel: "لسه مش متسجل كمدفوع",
    totalLabel: "الإجمالي",
    placedOnLabel: "تاريخ الطلب",
    qtyLabel: "الكمية",
    statusLabelPrefix: "الحالة",
    paymentLabelPrefix: "الدفع",
    lookupError: "معلش، حصل خطأ أثناء البحث. حاول تاني بعد شوية.",
    kitsIntro: "دي بعض أطقم الرسم عندنا:",
    kitsEmpty: "معندناش أطقم متاحة دلوقتي — تابعنا قريب!",
    kitsError: "معلش، مقدرتش أحمّل الأطقم دلوقتي. حاول تاني بعد شوية.",
    viewAllKits: "شوف كل أطقم الرسم",
    palettesIntro: "دي بعض باليتات الألوان عندنا:",
    palettesEmpty: "معندناش باليتات متاحة دلوقتي — تابعنا قريب!",
    palettesError: "معلش، مقدرتش أحمّل الباليتات دلوقتي. حاول تاني بعد شوية.",
    viewAllPalettes: "شوف كل باليتات الألوان",
    workshopsIntro: "دي الورش الجاية عندنا:",
    workshopsEmpty: "معندناش ورش متاحة دلوقتي — تابعنا قريب!",
    workshopsError: "معلش، مقدرتش أحمّل الورش دلوقتي. حاول تاني بعد شوية.",
    viewAllWorkshops: "شوف كل الورش",
    spotsLeft: (n) => `متبقي ${n} أماكن`,
    fullyBooked: "محجوز بالكامل",
    shippingPrompt: "هتشحن لأنهي محافظة؟",
    shippingSelectPlaceholder: "اختار محافظة...",
    shippingResult: (name, cost) => `الشحن لـ ${name} تكلفته ${cost} جنيه.`,
    shippingNotFound: "معلش، مش لاقي بيانات شحن للمحافظة دي.",
    shippingError: "معلش، مقدرتش أحمّل بيانات الشحن دلوقتي. حاول تاني بعد شوية.",
    contactIntro: "دي طريقة التواصل مع فريقنا مباشرة:",
    fallbackIntro:
      "أقدر أساعدك في تتبع الطلبات، أطقم الرسم، باليتات الألوان، الورش، وتكلفة الشحن. لأي حاجة تانية، فريقنا هيسعد يساعدك:",
    fallbackUnknown:
      "معلش، مش قادر أجاوب على السؤال ده. تقدر تتواصل معانا على واتساب أو إيميل تحت، أو تختار من الخيارات.",
    inputPlaceholder: "اكتب سؤالك...",
    closeChat: "قفل الشات",
    openChat: "افتح الشات",
  },
};

// Keyword matching for free-typed text, in both languages. \b word
// boundaries only work reliably for Latin script, so Arabic patterns are
// plain substring matches instead.
export function matchIntent(text: string): string | null {
  const t = text.toLowerCase();
  if (/\b(order|track|status|reference|ref)\b/.test(t) || /(طلب|تتبع|حالة|مرجع)/.test(text)) {
    return "track";
  }
  if (/\b(palette|palettes)\b/.test(t) || /(باليت|الوان|ألوان)/.test(text)) {
    return "palettes";
  }
  if (/\b(kit|kits|price|prices|buy)\b/.test(t) || /(طقم|اطقم|أطقم|سعر|اسعار|أسعار|شراء)/.test(text)) {
    return "kits";
  }
  if (/\b(workshop|workshops|class|session)\b/.test(t) || /(ورشة|ورش|كلاس|جلسة)/.test(text)) {
    return "workshops";
  }
  if (/\b(ship|shipping|deliver|delivery)\b/.test(t) || /(شحن|توصيل|تسليم)/.test(text)) {
    return "shipping";
  }
  if (
    /\b(contact|whatsapp|phone|call|email)\b/.test(t) ||
    /(تواصل|واتساب|رقم|تليفون|اتصال|ايميل|إيميل)/.test(text)
  ) {
    return "contact";
  }
  return null;
}
